import { useEffect, useState } from "react";
import {
    chainNowSec,
    getChainOffsetSec,
    chainOffsetAvailable,
    startChainClock
} from "../utils/chainClock.js";

/**
 * React access to the app's single chain clock (utils/chainClock.js).
 * Returns a chain-accurate "now" that re-evaluates every second so
 * consumers replaying a per-second interest/lifecycle formula can read
 * `chainNowSec` directly.
 *
 *     const { chainNowSec } = useChainTime();
 *     const accrued = liveAccruedInterestSmooth(p, start, maturity, chainNowSec);
 *
 * The actual offset (chain block timestamp − browser wall clock) is owned
 * by the chainClock module and synced from the backend's authoritative
 * GET /api/chain/time, so EVERY surface — pure utils, components, and the
 * backend itself — keys off the exact same clock. This matters under
 * local time-warp testing where chain time runs weeks ahead of wall time;
 * see chainClock.js for the full rationale.
 */
export function useChainTime({ pollMs = 30_000 } = {}) {
    // `tick` re-renders the consumer each second so the derived
    // `chainNowSec()` reading advances without an extra network poll.
    const [, setTick] = useState(0);

    useEffect(() => {
        startChainClock({ pollMs });
        const tickId = setInterval(() => setTick((t) => t + 1), 1000);
        return () => clearInterval(tickId);
    }, [pollMs]);

    return {
        chainNowSec: chainNowSec(),
        offsetSec: getChainOffsetSec(),
        available: chainOffsetAvailable()
    };
}
